import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import environment from '../../environment';

// Public, tokenized ConnectMeet (CM) rating page. Opened from the 7-day rating tickler email.
// Overall value 1..3 and per-participant value 1..3, plus a free-form comment.

const CmRate: React.FC = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [info, setInfo] = useState<any>(null);

    const [overall, setOverall] = useState<number | string>(0);
    const [peer, setPeer] = useState<Record<string, number | string>>({});
    const [freeform, setFreeform] = useState('');

    useEffect(() => {
        if (!token) { setLoading(false); setError('Missing rating link.'); return; }
        ApiClient.get('api/cm/rate/form', { token }).then((res: any) => {
            setLoading(false);
            if (res.success) {
                setInfo(res.data);
                if (res.data.alreadySubmitted) setDone(true);
            } else setError(res.message || 'This rating link is invalid or expired.');
        }).catch(() => { setLoading(false); setError('This rating link is invalid or expired.'); });
    }, [token]);

    const submit = () => {
        if (!overall) { setError('Please rate the overall value.'); return; }
        setError('');
        const participantRatings = (info?.others || []).map((o: any) => ({
            ratedUserId: o.userId, ratedEmail: o.email, ratedName: o.name, value: peer[o.email],
        })).filter((p: any) => p.value === NOT_RELEVANT || (typeof p.value === 'number' && p.value >= 1 && p.value <= 3));
        ApiClient.post('api/cm/rate/submit', { token, overallValue: overall, participantRatings, freeform }).then((res: any) => {
            if (res.success) setDone(true); else setError(res.message || 'Something went wrong.');
        }).catch(() => setError('Something went wrong.'));
    };

    const label = info?.label || 'ConnectMeet';

    if (loading) return <Center><p className="text-gray-500">Loading…</p></Center>;
    if (error && !info) return <Center><Card><h1 className="text-xl font-bold mb-2">Invalid link</h1><p className="text-gray-600">{error}</p></Card></Center>;
    if (done) return (
        <Center><Card>
            <div className="text-green-500 text-5xl mb-3">✓</div>
            <h1 className="text-2xl font-bold mb-2">Thank you!</h1>
            <p className="text-gray-600 mb-4">Your feedback helps your group get better {label} matches.</p>
            {environment?.api !== undefined && (
                <a href="/profile/personal-settings#cm-preferences" className="text-orange-500 text-sm">Update my preferences</a>
            )}
        </Card></Center>
    );

    return (
        <Center>
            <Card wide>
                <h1 className="text-2xl font-bold mb-1">Rate your {label}</h1>
                {info?.groupName && <p className="text-sm text-gray-500 mb-4">{info.groupName}</p>}

                <div className="text-xs text-gray-500 mb-5 space-y-1">
                    <p>Your answers are anonymous.</p>
                    <p>These are not reported per person.</p>
                    <p>This helps us match you better in the future.</p>
                    {info?.avgParticipantRating != null && (
                        <p>On average, a person receives {info.avgParticipantRating} rating</p>
                    )}
                </div>

                <p className="text-sm text-gray-600 mb-4">On a scale of 1 to 3, three being highest.</p>

                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">How much value was this {label}?</h3>
                    <div>
                        <Scale value={overall} onChange={setOverall} spread />
                        <div className="flex justify-between text-xs text-gray-400 mt-1 max-w-xs">
                            <span className="w-24 text-left leading-tight">Not a great use of my time</span>
                            <span className="w-24 text-right leading-tight">Enjoyable / useful</span>
                        </div>
                    </div>
                </div>

                {info?.others?.length > 0 && (
                    <div className="mb-6 space-y-5">
                        {info.others.map((o: any, i: number) => (
                            <div key={i}>
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                    How much value did {o.name} provide?
                                </h3>
                                <div>
                                    <Scale value={peer[o.email] || 0} onChange={(v) => setPeer(p => ({ ...p, [o.email]: v }))} spread />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Anything else you'd like to share about this {label}?</h3>
                    <textarea value={freeform} onChange={e => setFreeform(e.target.value)} rows={4} className="w-full border rounded p-2 text-sm" placeholder="Optional" />
                </div>

                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
                <button onClick={submit} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded">Submit feedback</button>
            </Card>
        </Center>
    );
};

// Sentinel recorded when the rater picks "Not relevant" (stored/sent as-is, ignored in averages).
const NOT_RELEVANT = 'N/A';

const Scale: React.FC<{ value: number | string; onChange: (v: number | string) => void; small?: boolean; spread?: boolean }> = ({ value, onChange, small }) => (
    <div className="flex items-center gap-2 flex-wrap">
        {[1, 2, 3].map(n => (
            <button key={n} type="button" onClick={() => onChange(n)}
                className={`${small ? 'w-8 h-8 text-sm' : 'w-11 h-11'} rounded-full border font-semibold ${value === n ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600'}`}>{n}</button>
        ))}
        <button type="button" onClick={() => onChange(NOT_RELEVANT)}
            className={`${small ? 'h-8 text-sm' : 'h-11'} px-4 rounded-full border font-semibold ${value === NOT_RELEVANT ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600'}`}>Not relevant</button>
    </div>
);

const Center: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-10 px-4">{children}</div>
);
const Card: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
    <div className={`bg-white p-6 md:p-8 rounded-lg shadow-md w-full ${wide ? 'max-w-xl' : 'max-w-md text-center'}`}>{children}</div>
);

export default CmRate;
